import React, { useCallback, useState } from 'react'
import { useIntl, FormattedMessage } from 'react-intl'
import { useAccess } from 'contexts/access'
import { useOrganization } from 'contexts/organization'
import { useWhistleblowing } from 'contexts/whistleblowing'
import { useWhistleblowerPortalUrl } from 'pages/whistleblowing/utilities'
import { useNavigate } from 'react-router-dom'
import { useLocationStateIntent } from 'hooks/location-intent'
import paths from 'app/paths'
import { HeroColumns, TitleColumns, ActionColumn, PortalLink } from './s'
import { ExternalLink } from 'styled-icons/feather'
import ModuleTitle from 'components/module-title'
import { Ghost, ButtonArrow } from 'components/button'
import Modal from 'modals/dialog'
import SetUp from 'pages/whistleblowing/modals/set-up'
import UpgradeModal from 'modals/upgrade'
import Message from 'components/message'

const WhistleblowingHero = ({ upgradable, unconfigured, salt }) => {
    const { formatMessage } = useIntl()

    const {
        check,
        refresh: refreshAccess
    } = useAccess()

    const admin = check('organization:manage')
    const handler = check('whistleblowing:manage')

    const { refreshStatuses } = useOrganization()

    const { activate } = useWhistleblowing()
    const whistleblowerPortalUrl = useWhistleblowerPortalUrl()

    const navigate = useNavigate()

    const navigateToSettings = useCallback(
        (grantedMyself = false) => navigate(
            (handler || grantedMyself) ?
                paths.whistleblowing.settings :
                paths.whistleblowing.base,
            { replace: true }),
        [handler]
    )

    const [settingUp, setSettingUp] = useState(false)
    const [upgrading, setUpgrading] = useState(false)

    const reactivate = async () => {
        const { ok } = await activate()
        ok && navigateToSettings()
    }

    const activateAction = useCallback(() => {
        if(!unconfigured) {
            return reactivate()
        }

        setSettingUp(true)
    }, [unconfigured])

    useLocationStateIntent({
        intent: 'set-up',
        action: activateAction,
        requirements: [admin]
    })

    return (
        <>
            <HeroColumns>
                <ModuleTitle which="whistleblowing" />
                {((admin || upgradable) || handler) && (
                    <TitleColumns>
                        {((admin || upgradable) && unconfigured) && (
                            <ActionColumn>
                                <Ghost
                                    onClick={activateAction}
                                    className="constructive"
                                    disabled={upgrading}
                                    {...(upgradable ? {
                                        upgradable: true,
                                        onClick: () => setUpgrading(true)
                                    } : null)}>
                                    <FormattedMessage
                                        id="whistleblowing_action_set_up"
                                        defaultMessage="Set up whistleblowing…" />
                                </Ghost>
                                {!upgradable && <ButtonArrow />}
                            </ActionColumn>
                        )}
                        {(((admin && !upgradable) || handler) && !unconfigured) && (
                            <ActionColumn>
                                <PortalLink
                                    href={whistleblowerPortalUrl}
                                    target="_blank"
                                    className="constructive">
                                    <FormattedMessage
                                        id="noun_whistleblower_portal"
                                        defaultMessage="Whistleblower portal" />
                                    <ExternalLink size={16} />
                                </PortalLink>
                            </ActionColumn>
                        )}
                    </TitleColumns>
                )}
            </HeroColumns>
            {(!admin && !upgradable && unconfigured) && (
                <Message
                    type="info"
                    message={formatMessage({
                        id: 'whistleblowing_message_not_activated',
                        defaultMessage: 'The Whistleblowing module is not yet activated. Ask your administrator to activate and set it up before you can start using it.'
                    })} />
            )}
            {(!!admin && !upgradable && unconfigured) && (
                <Modal
                    show={settingUp}
                    dismiss={() => setSettingUp(false)}
                    className="scrollable"
                    salt={`${salt}:set-up`}>
                    <SetUp
                        onDone={async (grantedMyself = false) => {
                            grantedMyself && await refreshAccess()
                            await refreshStatuses()

                            setSettingUp(false)
                            navigateToSettings(grantedMyself)
                        }}
                        salt={`${salt}:set-up`} />
                </Modal>
            )}
            {upgradable && (
                <UpgradeModal
                    heading={formatMessage({
                        id: 'whistleblowing_action_set_up',
                        defaultMessage: 'Set up whistleblowing…'
                    })}
                    dismiss={() => setUpgrading(false)}
                    {...(upgrading ? {
                        upgrade: { module: 'whistleblowing' }
                    } : null)}
                    callback={activateAction}
                    salt={`${salt}:upgrade`}>
                </UpgradeModal>
            )}
        </>
    )
}

export default WhistleblowingHero